// Tag colors. Each Tag persists one of these names on `tags.color` (see
// Tag::COLORS on the Rails side — keep the two lists in sync). A tag badge
// shows a *faded* background with *full-color* text + icon, so each entry
// pairs a soft tinted surface with a saturated foreground.
//
// The class strings are written out literally (not interpolated) so Tailwind
// v4's JIT compiler — which scans source for class names via the `@source`
// directive — actually emits them. Don't refactor these into
// `bg-${name}-100`; that breaks the build silently.
export const TAG_COLOR_NAMES = [
  "rose", "red", "orange", "amber", "yellow",
  "lime", "green", "emerald", "teal", "cyan",
  "sky", "blue", "indigo", "violet", "fuchsia",
] as const
export type TagColorName = (typeof TAG_COLOR_NAMES)[number]

export type TagColor = {
  // Combined badge classes — faded background + full-color text. The icon
  // inherits `currentColor`, so it picks up the text color too.
  badge: string
  // Solid swatch — the dot shown in the color picker.
  swatch: string
}

const COLORS: Record<TagColorName, TagColor> = {
  rose:    { badge: "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300",          swatch: "bg-rose-500" },
  red:     { badge: "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300",              swatch: "bg-red-500" },
  orange:  { badge: "bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300",  swatch: "bg-orange-500" },
  amber:   { badge: "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300",      swatch: "bg-amber-500" },
  yellow:  { badge: "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-300",  swatch: "bg-yellow-500" },
  lime:    { badge: "bg-lime-100 text-lime-800 dark:bg-lime-950/50 dark:text-lime-300",          swatch: "bg-lime-500" },
  green:   { badge: "bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-300",       swatch: "bg-green-500" },
  emerald: { badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300", swatch: "bg-emerald-500" },
  teal:    { badge: "bg-teal-100 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300",          swatch: "bg-teal-500" },
  cyan:    { badge: "bg-cyan-100 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-300",          swatch: "bg-cyan-500" },
  sky:     { badge: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",              swatch: "bg-sky-500" },
  blue:    { badge: "bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",          swatch: "bg-blue-600" },
  indigo:  { badge: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300",  swatch: "bg-indigo-500" },
  violet:  { badge: "bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",  swatch: "bg-violet-500" },
  fuchsia: { badge: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-950/50 dark:text-fuchsia-300", swatch: "bg-fuchsia-500" },
}

const FALLBACK: TagColor = {
  badge:  "bg-zinc-100 text-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300",
  swatch: "bg-zinc-400",
}

export function tagColor(color: string | null | undefined): TagColor {
  if (!color) return FALLBACK
  return COLORS[color as TagColorName] || FALLBACK
}
